import React, {
    ReactElement, useEffect, useState,
} from "react";
import EditableControl, {
    FORM_FIELD, RecursiveOption, ValueTypes,
} from "../editable-text/EditableText";
import { HtmlRenderer } from "../html-render/HtmlRenderer";
import {
    AsyncObjectType,
    CustomFieldValue,
    FieldTypedReplaceableValue,
    FieldTypedValue, FieldTypedValueBase, FieldTypedValueBaseInput,
    FieldTypedValueFile,
    FieldTypedValueInput, FormField, FormFieldOptionSettings, FormFieldReplacementInput,
    FormFieldType, FormFieldVisibility, WorkflowFormField, WorkflowFormFieldSchema,
} from "../../__generated__/api";
import { ReplacementGroup } from "../replacement-group";
import Theme from "../../pages/site-admin/sections/theme/Theme";
import { useTheme } from "../../contexts/Theme";
import Text from "../theme/text/Text";
import FormGroup from "../theme/form-group/FormGroup";

interface FieldTypeValueDisplayProps {
    field: FormField | WorkflowFormField;
    onChange: (value: FieldTypedValue, replacements?: FormFieldReplacementInput[]) => void;
    className?: string;
    inputClassName?: string;
    value?: FieldTypedValue | FieldTypedValueBase | FieldTypedValueBaseInput;
    shouldShowFieldName?: boolean;
    shouldShowDescription?: boolean;
    replacements?: FormFieldReplacementInput[];
    replaceableInput?: ReplacementGroup[];
    defaultValue?: FieldTypedReplaceableValue;
    integrationId?: string;
    fieldNameClassName?: string;
    shouldFocus?: boolean;
    getCurrentForm?: () => FieldTypedValueInput[];
    replacementInsertLabel?: string;
    useFormGroup?: boolean;
    shouldUseEditor?: boolean;
}

export const REPLACE_DEFAULT_GROUP = "urn:fq:form-field-group:default-properties";
export const REPLACE_NOW_DATE = "urn:fq:form-field:date-now";
export const REPLACE_NOW_TIME = "urn:fq:form-field:time-now";
export const REPLACE_EMPTY_USER = "urn:fq:form-field:empty-user";
export const REPLACE_EMPTY_USERS = "urn:fq:form-field:empty-users";
export const REPLACE_CURRENT_USER = "urn:fq:form-field:current-user";
export const REPLACE_EMPTY_BOOLEAN = "urn:fq:form-field:empty-boolean";

export const DEFAULT_VALUE_REPLACEMENTS = [
    {
        id: REPLACE_DEFAULT_GROUP,
        description: "Properties",
        label: "Default Properties",
        input: [
            {
                id: REPLACE_NOW_DATE,
                name: "Current Date",
                type: FormFieldType.Date,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            {
                id: REPLACE_NOW_DATE,
                name: "Current Date and Time",
                type: FormFieldType.DateTime,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            {
                id: REPLACE_NOW_TIME,
                name: "Current Time",
                type: FormFieldType.Time,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            {
                id: REPLACE_EMPTY_USER,
                name: "No User",
                type: FormFieldType.User,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            {
                id: REPLACE_CURRENT_USER,
                name: "Current User",
                type: FormFieldType.User,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            {
                id: REPLACE_EMPTY_BOOLEAN,
                name: "Empty Value",
                type: FormFieldType.Boolean,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            },
            // {
            //     id: REPLACE_EMPTY_USERS,
            //     name: "No Users",
            //     type: FormFieldType.Users,
            //     isSystemField: false,
            //     visibility: FormFieldVisibility.Private,
            // },
        ],
    },
];

export function getValue(
    valueField: FieldTypedValue | FieldTypedValueBaseInput | FieldTypedValueBase | CustomFieldValue | undefined,
    type: FormFieldType,
    defaultValue?: FieldTypedReplaceableValue,
    replacements?: FormFieldReplacementInput[],
) {
    if (type === FormFieldType.MultiSelectDropdown
        || type === FormFieldType.MultiLevelDropdown) {
        return valueField?.options ?? defaultValue?.value?.options;
    }
    if (type === FormFieldType.Boolean) {
        if (defaultValue?.value?.string
            && defaultValue?.replacements?.find(replace => replace.fieldId === REPLACE_EMPTY_BOOLEAN)) {
            return undefined;
        }
        if (replacements?.isNotEmpty && valueField?.string) {
            return valueField?.string;
        }

        return (replacements?.isNotEmpty ? valueField?.string : valueField?.boolean) ?? defaultValue?.value?.boolean;
    }
    if (type === FormFieldType.Date || type === FormFieldType.Time || type === FormFieldType.DateTime) {
        if (replacements?.isNotEmpty && valueField?.string) {
            return valueField?.string;
        }
        if (valueField?.date) {
            return new Date(valueField?.date);
        }
        if (defaultValue?.value?.string && defaultValue?.replacements?.isNotEmpty) {
            let value: string | Date = defaultValue?.value?.string;
            defaultValue?.replacements.forEach(replace => {
                if (replace.fieldId === REPLACE_NOW_DATE || replace.fieldId === REPLACE_NOW_TIME) {
                    value = new Date();
                }
            });
            return new Date(value);
        }

        return defaultValue?.value?.date ? new Date(defaultValue?.value?.date) : undefined;
    }
    if (type === FormFieldType.Number || type === FormFieldType.Decimal) {
        return replacements?.isNotEmpty ? valueField?.string : valueField?.number;
    }
    if (type === FormFieldType.User) {
        return valueField?.string
            ?? (defaultValue?.replacements?.some(rep => rep.fieldId === REPLACE_EMPTY_USER)
                ? ""
                : defaultValue?.value.string);
    }
    if (type === FormFieldType.Users) {
        return valueField?.options
            ?? (defaultValue?.replacements?.some(rep => rep.fieldId === REPLACE_EMPTY_USERS)
                ? ""
                : defaultValue?.value.options);
    }
    if (valueField?.options) {
        return valueField?.options ?? defaultValue?.value?.options;
    }
    if (valueField?.files) {
        return valueField?.files ?? defaultValue?.value?.options;
    }
    return valueField?.string ?? valueField?.number ?? defaultValue?.value?.string;
}

export default function FieldTypeValueDisplay({
    field, onChange, className, inputClassName, value: valueField, replaceableInput, replacements, shouldShowFieldName,
    defaultValue, integrationId, fieldNameClassName, getCurrentForm, shouldFocus, replacementInsertLabel, shouldShowDescription,
    useFormGroup, shouldUseEditor,
}: FieldTypeValueDisplayProps): ReactElement {
    const [display, setDisplay] = useState<string>();
    useEffect(() => {
        setDisplay(valueField?.displayValue?.trim());
    }, [valueField]);

    const type = field?.type ?? valueField?.type;
    const value = getValue(valueField, type, defaultValue, replacements);

    const theme = useTheme();

    const control = (
        <EditableControl
            shouldUseEditor={shouldUseEditor}
            isReadOnly={false}
            isEditing
            isFormField
            asyncObjectType={field.asyncObjectType as AsyncObjectType | undefined}
            shouldFocus={shouldFocus ?? false}
            isRequired={field.isRequired ?? true}
            inputClassName={inputClassName}
            placeholder={field.placeholder || `Enter ${field?.name}`}
            type={FORM_FIELD[type as FormFieldType]}
            jsonSchema={(field as WorkflowFormField).schema as WorkflowFormFieldSchema}
            options={field?.options as RecursiveOption<unknown>[]}
            replaceableInput={replaceableInput}
            replacementInsertLabel={replacementInsertLabel}
            value={value as ValueTypes}
            integrationId={integrationId}
            optionsSettings={field.optionsSettings as FormFieldOptionSettings}
            replacements={replacements}
            labels={field?.levels as string[]}
            getCurrentForm={getCurrentForm}
            onChange={(changed, replaceValues, displayText) => {
                const changedValue: FieldTypedValue = Object.create(null);
                changedValue.displayValue = displayText ?? changed?.toString();
                changedValue.fieldId = field.id;
                changedValue.type = type;

                if (replaceValues?.isNotEmpty && (type === FormFieldType.Boolean
                    || type === FormFieldType.Number
                    || type === FormFieldType.Date
                    || type === FormFieldType.Time
                    || type === FormFieldType.DateTime
                )) {
                    changedValue.string = changed as string;
                } else if (type === FormFieldType.Boolean) {
                    changedValue.boolean = changed as boolean;
                } else if (type === FormFieldType.Number) {
                    changedValue.number = parseFloat(changed as string);
                } else if (type === FormFieldType.Time) {
                    changedValue.date = new Date(`0000-01-01T${changed}:00Z`);
                } else if (type === FormFieldType.Date || type === FormFieldType.DateTime) {
                    changedValue.date = new Date(changed as string);
                } else if (type === FormFieldType.MultiLevelDropdown
                    || type === FormFieldType.MultiSelectDropdown
                    || type === FormFieldType.Checkboxes) {
                    changedValue.options = changed as string[];
                } else if (type === FormFieldType.Files) {
                    changedValue.files = changed as FieldTypedValueFile[];
                } else if (Array.isArray(changed)) {
                    changedValue.options = changed as string[];
                } else {
                    changedValue.string = changed as string;
                }

                onChange(changedValue, replaceValues);
            }}
        >
            {display ? (
                <span>
                    {display}
                </span>
            ) : undefined}
        </EditableControl>
    );

    if (useFormGroup) {
        return (
            <FormGroup
                groupClassName={className}
                label={field.name}
                isRequired={field.isRequired ?? false}
                helpText={field.description}
            >
                {control}
            </FormGroup>
        );
    }

    return (
        <div className={className} key={field.id}>
            {shouldShowFieldName && (
                <strong className={fieldNameClassName}>
                    {field.name}
                    {" "}
                    {field.isRequired === false && (
                        <small className="text-light">(optional)</small>
                    )}

                </strong>
            )}

            {control}

            {field.description && shouldShowDescription && (
                theme.isV3 ? (
                    <Text as="small" textVariant="body-2-regular" colorVariant="body-secondary">
                        <HtmlRenderer html={field.description ?? ""} />
                    </Text>
                ) : (
                    <small className="text-gray-600">
                        <HtmlRenderer html={field.description ?? ""} />
                    </small>
                )
            )}
        </div>
    );
}
FieldTypeValueDisplay.defaultProps = {
    inputClassName: undefined,
    className: "",
    value: undefined,
    replaceableInput: undefined,
    replacements: undefined,
    shouldShowFieldName: true,
    defaultValue: undefined,
    integrationId: undefined,
    fieldNameClassName: "",
    getCurrentForm: undefined,
    shouldFocus: false,
    replacementInsertLabel: undefined,
    shouldShowDescription: true,
    useFormGroup: false,
    shouldUseEditor: false,
};
